/** Generic Node Detail renderer (spec §15/§20) — the "职责拆分" fallback
 * every node type without a dedicated renderer gets. This is the exact
 * same small per-type field editor that used to live in the right
 * Inspector (`RightInspectorPanel`'s `renderTypeSpecificFields`),
 * relocated below the node instead of removed — the content itself
 * (dialogue text/speaker, character description, scene notes, …) isn't
 * new business logic, only its position moved, so this already
 * satisfies "content/user-facing parameters migrate to the Node Detail
 * Panel" for every node type this round without a bespoke renderer
 * (spec §21: PHASE C — other node types migrate to their OWN renderer
 * later; until then they get this instead of nothing). */

import { Input } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import type { NodeDetailRendererProps } from '../types'

const { TextArea } = Input

export default function GenericDetailRenderer({ node, onChangeFields }: NodeDetailRendererProps) {
  const { t } = useTranslation('canvas')
  const { nodeLabel, categoryLabel } = useCanvasNodeRegistryI18n()
  const def = useNodeDefinition(node.node_type)
  const fields = node.data || {}
  const label = (fields.label as string) || nodeLabel(node.node_type, def.label)

  const setField = (key: string, value: unknown) => {
    onChangeFields(node.id, { ...fields, [key]: value })
  }

  return (
    <div className="pcv-detail-generic">
      <label className="pcv-field-label">{t('detail.name')}</label>
      <Input value={label} onChange={(e) => setField('label', e.target.value)} />

      {renderTypeSpecificFields(node.node_type, fields, setField, t)}

      <div className="pcv-inspector-meta">
        <div className="pcv-meta-row">
          <span>{t('detail.type')}</span>
          <span>{nodeLabel(node.node_type, def.label)}</span>
        </div>
        <div className="pcv-meta-row">
          <span>{t('detail.category')}</span>
          <span>{categoryLabel(node.category)}</span>
        </div>
        {node.entity_type ? (
          <div className="pcv-meta-row">
            <span>{t('detail.linkedEntity')}</span>
            <span>
              {node.entity_type}
              {node.entity_id ? ` · ${node.entity_id.slice(0, 8)}…` : t('detail.notLinked')}
            </span>
          </div>
        ) : null}
        {def.family === 'action' ? (
          <div className="pcv-meta-row">
            <span>{t('detail.lastRun')}</span>
            <span>{node.latest_run_id ? node.latest_run_id.slice(0, 8) : t('detail.notRunYet')}</span>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function renderTypeSpecificFields(
  nodeType: string,
  fields: Record<string, unknown>,
  setField: (key: string, value: unknown) => void,
  t: ReturnType<typeof useTranslation<'canvas'>>['t'],
) {
  switch (nodeType) {
    case 'dialogue':
      return (
        <>
          <label className="pcv-field-label">{t('detail.dialogueContent')}</label>
          <TextArea
            rows={4}
            value={(fields.text as string) || ''}
            onChange={(e) => setField('text', e.target.value)}
            placeholder={t('detail.dialoguePlaceholder')}
          />
          <label className="pcv-field-label">{t('detail.speaker')}</label>
          <Input value={(fields.speaker as string) || ''} onChange={(e) => setField('speaker', e.target.value)} />
        </>
      )
    case 'voice':
      return (
        <>
          <label className="pcv-field-label">{t('detail.speaker')}</label>
          <Input value={(fields.speaker as string) || ''} onChange={(e) => setField('speaker', e.target.value)} />
          <div className="pcv-field-hint">{t('detail.voicePreviewHint')}</div>
        </>
      )
    case 'character':
      return (
        <>
          <label className="pcv-field-label">{t('detail.characterDescription')}</label>
          <TextArea
            rows={3}
            value={(fields.description as string) || ''}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('detail.characterPlaceholder')}
          />
        </>
      )
    case 'reference_content':
      return (
        <>
          <label className="pcv-field-label">{t('detail.sourcePlatform')}</label>
          <Input value={(fields.platform as string) || ''} onChange={(e) => setField('platform', e.target.value)} />
        </>
      )
    case 'scene':
      return (
        <>
          <label className="pcv-field-label">{t('detail.sceneNotes')}</label>
          <TextArea
            rows={3}
            value={(fields.description as string) || ''}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('detail.scenePlaceholder')}
          />
        </>
      )
    case 'text_note':
      return (
        <>
          <label className="pcv-field-label">{t('detail.content')}</label>
          <TextArea rows={5} value={(fields.text as string) || ''} onChange={(e) => setField('text', e.target.value)} />
        </>
      )
    default:
      return null
  }
}
